/** FlexxiPlan (instalments module) — shared types and display labels. */

export const FLEXXI_PLAN_STATUSES = [
  "PENDING_DEPOSIT",
  "ACTIVE",
  "OVERDUE",
  "DEFAULTED",
  "COMPLETED",
  "CANCELLED",
] as const;

export type FlexxiPlanStatus = (typeof FLEXXI_PLAN_STATUSES)[number];

export const FLEXXI_CHANNELS = ["SHOP", "MARKETPLACE", "PACK", "AUCTION", "RAFFLE"] as const;
export type FlexxiChannel = (typeof FLEXXI_CHANNELS)[number];

export const FLEXXI_PAY_MODES = ["MIN_DUE", "ALL_OVERDUE", "FULL_REMAINING", "CUSTOM"] as const;
export type FlexxiPayMode = (typeof FLEXXI_PAY_MODES)[number];

export const FLEXXI_DEFAULT_DISPOSITIONS = ["PENDING", "RETURNED_TO_STOCK", "WRITTEN_OFF", "OTHER"] as const;
export type FlexxiDefaultDisposition = (typeof FLEXXI_DEFAULT_DISPOSITIONS)[number];

export const FLEXXI_PLAN_STATUS_LABEL: Record<FlexxiPlanStatus, { customer: string; staff: string }> = {
  PENDING_DEPOSIT: { customer: "Awaiting first payment", staff: "Pending first payment" },
  ACTIVE: { customer: "On plan", staff: "Active" },
  OVERDUE: { customer: "Payment overdue", staff: "Overdue" },
  DEFAULTED: { customer: "Plan defaulted", staff: "Defaulted" },
  COMPLETED: { customer: "Paid off", staff: "Completed" },
  CANCELLED: { customer: "Cancelled", staff: "Cancelled" },
};

export interface FlexxiSlotTier {
  minScore: number;
  maxActivePlans: number;
  allowedTemplateCodes: string[];
}

export interface FlexxiReputationWeights {
  onTimePayment: number;
  latePayment: number;
  partialPayment: number;
  completedPlan: number;
  defaultedPlan: number;
  marketplaceRatingBonus?: number;
}

export const DEFAULT_FLEXXI_SLOT_TIERS: FlexxiSlotTier[] = [
  { minScore: 0, maxActivePlans: 1, allowedTemplateCodes: ["3M"] },
  { minScore: 50, maxActivePlans: 2, allowedTemplateCodes: ["3M", "12M"] },
  { minScore: 100, maxActivePlans: 3, allowedTemplateCodes: ["3M", "12M"] },
];

export const DEFAULT_FLEXXI_REPUTATION_WEIGHTS: FlexxiReputationWeights = {
  onTimePayment: 2,
  latePayment: -5,
  partialPayment: 0,
  completedPlan: 15,
  defaultedPlan: -30,
  marketplaceRatingBonus: 1,
};

export function tierForScore(tiers: FlexxiSlotTier[], score: number): FlexxiSlotTier {
  const sorted = [...tiers].sort((a, b) => b.minScore - a.minScore);
  return sorted.find((t) => score >= t.minScore) ?? sorted[sorted.length - 1]!;
}

/** Staff-facing label for slot tier from reputation score. */
export function flexxiTierLabel(tiers: FlexxiSlotTier[], score: number): string {
  const tier = tierForScore(tiers, score);
  const sortedAsc = [...tiers].sort((a, b) => a.minScore - b.minScore);
  const rank = sortedAsc.findIndex((t) => t.minScore === tier.minScore);
  const tierName = rank >= 0 ? `Tier ${rank + 1}` : "Tier 1";
  return `${tierName} · ${tier.minScore}+ · ${tier.maxActivePlans} slot${tier.maxActivePlans === 1 ? "" : "s"}`;
}

export type FlexxiEligibleMode = "ALL" | "CATEGORIES" | "PRODUCTS";

export const FLEXXI_KYC_PROFILE_STATUSES = [
  "UNVERIFIED",
  "PENDING",
  "VERIFIED",
  "REJECTED",
  "UPDATE_REQUIRED",
] as const;

export type FlexxiKycProfileStatus = (typeof FLEXXI_KYC_PROFILE_STATUSES)[number];

export const FLEXXI_KYC_STATUS_LABELS: Record<FlexxiKycProfileStatus, string> = {
  UNVERIFIED: "Not verified",
  PENDING: "Under review",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
  UPDATE_REQUIRED: "Update required",
};

export const FLEXXI_KYC_GATE_CODE = "flexxi_kyc_required";

export function flexxiKycGateMessage(): string {
  return "Verify your identity to use FlexxiPlan instalments.";
}

export const FLEXXI_UPFRONT_MODES = ["DEPOSIT", "FIRST_INSTALLMENT"] as const;
export type FlexxiUpfrontMode = (typeof FLEXXI_UPFRONT_MODES)[number];

export const FLEXXI_UPFRONT_MODE_LABELS: Record<FlexxiUpfrontMode, string> = {
  DEPOSIT: "Deposit (% of price)",
  FIRST_INSTALLMENT: "First monthly instalment (no deposit)",
};

export interface FlexxiQuoteInstallment {
  sequence: number;
  dueDate: string;
  amountDue: number;
  /** True when this instalment is collected at checkout (first-instalment mode). */
  dueToday?: boolean;
}

export interface FlexxiQuote {
  templateId: string;
  templateCode: string;
  templateName: string;
  termMonths: number;
  upfrontMode: FlexxiUpfrontMode;
  /** Deposit % (0 in first-instalment mode). */
  depositPercent: number;
  principal: number;
  depositAmount: number;
  flexxiFeeAmount: number;
  interestTotal: number;
  financedAmount: number;
  totalPayable: number;
  dueToday: number;
  monthlyAmount: number;
  installments: FlexxiQuoteInstallment[];
}
